import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CorteCajaService } from '../../services/corte-caja.service';
import { AuthService } from '../../services/auth.service';
import moment from 'moment';
import { take } from 'rxjs/operators';

export interface CorteCajaDialogData {
  modo: 'crear' | 'ver' | 'cerrar';
  corte?: any;
}

@Component({
  selector: 'app-corte-caja-dialog',
  templateUrl: './corte-caja-dialog.component.html',
  styleUrls: ['./corte-caja-dialog.component.scss']
})
export class CorteCajaDialogComponent implements OnInit {
  isLoading = false;
  crearForm: FormGroup;
  cerrarForm: FormGroup;
  detalleCorte: any = null;
  loadingDetalle = false;

  currentUserId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CorteCajaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CorteCajaDialogData,
    private corteCajaService: CorteCajaService,
    private authService: AuthService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.crearForm = this.fb.group({
      fechaInicio: [moment().startOf('day').toDate(), Validators.required],
      fechaFin: [moment().endOf('day').toDate(), Validators.required],
      observaciones: ['']
    });

    this.cerrarForm = this.fb.group({
      efectivoContado: [null, [Validators.required, Validators.min(0)]],
      observaciones: ['']
    });

    if (this.data.modo === 'crear') {
      this.obtenerUsuarioActual();
    }

    if (this.data.modo === 'ver' && this.data.corte?.id) {
      this.cargarDetalle(this.data.corte.id);
    }
  }

  private obtenerUsuarioActual(): void {
    const snap = this.authService.getCurrentUserSnapshot();
    if (snap?.['userId'] || snap?.['id']) {
      this.currentUserId = snap['userId'] ?? snap['id'];
    } else {
      this.authService.currentUser$.pipe(take(1)).subscribe(user => {
        this.currentUserId = user?.['userId'] ?? user?.['id'] ?? null;
      });
    }
  }

  private cargarDetalle(id: number): void {
    this.loadingDetalle = true;
    this.corteCajaService.getDetalle(id).subscribe({
      next: (detalle) => {
        this.detalleCorte = detalle;
        this.loadingDetalle = false;
      },
      error: () => {
        this.toastr.error('Error al cargar el detalle del corte');
        this.loadingDetalle = false;
      }
    });
  }

  get titulo(): string {
    switch (this.data.modo) {
      case 'crear': return 'Nuevo Corte de Caja';
      case 'ver': return 'Detalle del Corte';
      case 'cerrar': return 'Cerrar Corte de Caja';
    }
  }

  confirmarCrear(): void {
    if (this.crearForm.invalid) return;
    if (this.isLoading) return;

    const { fechaInicio, fechaFin, observaciones } = this.crearForm.value;

    const dto: any = {
      fechaInicio: moment(fechaInicio).startOf('day').toISOString(),
      fechaFin: moment(fechaFin).endOf('day').toISOString(),
    };
    if (this.currentUserId) dto.usuarioId = this.currentUserId;
    if (observaciones?.trim()) dto.observaciones = observaciones.trim();

    this.isLoading = true;
    this.corteCajaService.crearCorte(dto).subscribe({
      next: () => {
        this.toastr.success('Corte de caja creado exitosamente');
        this.dialogRef.close(true);
      },
      error: (err) => {
        const msg = err?.error?.message || 'Error al crear el corte';
        this.toastr.error(msg);
        this.isLoading = false;
      }
    });
  }

  confirmarCerrar(): void {
    if (this.cerrarForm.invalid) return;
    if (this.isLoading) return;

    const { efectivoContado, observaciones } = this.cerrarForm.value;
    const dto: any = { efectivoContado: Number(efectivoContado) };
    if (observaciones?.trim()) dto.observaciones = observaciones.trim();

    this.isLoading = true;
    this.corteCajaService.cerrarCorte(this.data.corte.id, dto).subscribe({
      next: () => {
        this.toastr.success('Corte cerrado exitosamente');
        this.dialogRef.close(true);
      },
      error: (err) => {
        const msg = err?.error?.message || 'Error al cerrar el corte';
        this.toastr.error(msg);
        this.isLoading = false;
      }
    });
  }

  cancelar(): void {
    this.dialogRef.close(false);
  }

  get diferenciaPreview(): number | null {
    const corte = this.data.corte;
    const contado = this.cerrarForm.get('efectivoContado')?.value;
    if (corte == null || contado == null) return null;
    return Number(contado) - Number(corte.totalEfectivo ?? 0);
  }

  formatCurrency(val: any): string {
    if (val == null) return '—';
    return `$${Number(val).toFixed(2)}`;
  }

  formatDate(val: any): string {
    if (!val) return '—';
    return moment(val).format('DD/MM/YYYY HH:mm');
  }
}
